/* eslint-disable no-shadow */
// import { RowDataPacket } from 'mysql2';

export const CorsHeaders = {
  'Access-Control-Allow-Methods': '*',
  'Access-Control-Allow-Headers': '*',
  'Access-Control-Allow-Origin': '*',
};

/*
 * Enum Definition
 */

export enum ApprovalAction {
  APPROVE = 'approve',
  REJECT = 'reject',
}

export enum Language {
  JAPANESE = 'ja',
  ENGLISH = 'en',
}

export enum PostStatus {
  DRAFT = 'draft',
  REVIEW = 'review',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
  UNPUBLISHED = 'unpublished',
  DELETED = 'deleted',
}

export enum Severity {
  LOW = 'low',
  NORMAL = 'normal',
  HIGH = 'high',
  URGENT = 'urgent',
  CRITICAL = 'critical',
}

export enum TaskStatus {
  TODO = 'to-do',
  IN_PROGRESS = 'in-progress',
  DONE = 'done',
  CANCELED = 'canceled',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum TaskType {
  APPROVAL = 'approval',
  COMMENT_APPROVAL = 'comment-approval',
  INQUIRY = 'inquiry',
}

// TODO: sync with feature role matrix
export enum UserRole {
  MR = 'mr',
  MR_APPROVER = 'mr-approver',
  RM = 'rm',
  PMO = 'pmo',
  PMO_APPROVER = 'pmo-approver',
  HQ = 'hq',
}

/*
 * Input Parameter Definition
 */

// POST /inquiries/{id}/answers
export type CreateAnswerInput = {
  body: string;
};

// PUT /inquiries/{id}/answers/{answerId}
export type UpdateAnswerInput = CreateAnswerInput;

// DELETE /inquiries/{id}/answers/{answerId}
export type DeleteAnswerInput = CreateAnswerInput;

// POST /comments
export type CreateCommentInput = {
  body: string;
  status: PostStatus;
};

// PUT /posts/{id}/comments/{commentsId}
export type UpdateCommentInput = {
  body: string;
  status: PostStatus;
};

// POST /inquiries
export type CreateInquiryInput = {
  groupIds: Array<number | undefined>;
  title: string;
  body: string;
  url: string;
  dueOn: string;
};

// PATCH /inquiries/{id}
export type UpdateInquiryInput = {
  title: string;
  body: string;
  url: string;
  dueOn: string;
};

// POST /notifications
export type CreateNotificationInput = {
  title: string;
  body: string;
  isImportant: boolean;
};

// POST /posts
export type CreatePostInput = {
  quotedPostId?: number;
  title: string;
  body: string;
  occupationGroupId?: number;
  occupationId?: number;
  workplaceId?: number;
  diseaseGroupId?: number;
  diseaseId?: number;
  productGroupId?: number;
  competitiveProductId?: number;
  categoryId?: number;
  activityId?: number;
  status: PostStatus;
  attachments?: string[];
};

// PATCH /posts/{id}
// REVIEW: Whether the quotation can be changed after creating a new post.
export type UpdatePostInput = CreatePostInput & {
  deletedIds?: number[];
};

// POST /reactions
export type CreateReactionInput = {
  postId: number;
  reactionTypeId: number;
};

// PUT /reactions/{id}
export type UpdateReactionInput = CreateReactionInput;

// PATCH /tasks/{id}
export type UpdateTaskInput = {
  action: ApprovalAction;
  comment: string;
};

// PATCH /users/{id}
export type UpdateUserInput = {
  agreedAt: string;
};

// PUT /users/{id}/notifications/{notificationId}
export type UpdateUsersNotificationsInput = {
  isRead: boolean;
};

// POST /users/{id}/subscriptions
export type CreateSubscriptionsInput = {
  subscription: string;
};

// POST /signedUrl
export type FileInfo = {
  fileName: string;
  fileType: string;
};

/*
 * Output Schema Definition
 */
/*
export interface JsonString extends RowDataPacket {
  json: string;
}
*/

export type Data<T> = {
  data: Array<T | null>;
  fetchedAt?: string;
  next: string;
};

// GET /attributes
export type Attribute = {
  languageCode: Language;
  occupationGroups: Array<OccupationGroup>;
  workplaces: Array<Workplace>;
  diseaseGroups: Array<DiseaseGroup>;
  categories: Array<Category>;
  productGroups: Array<ProductGroup>;
  competitiveProducts: Array<Product>;
};

export type SimpleMasterData = {
  id: number;
  name: string;
};

export type OccupationGroup = {
  id: number;
  name: string;
  occupations: Array<Occupation>;
};

export type Occupation = SimpleMasterData;

export type Workplace = SimpleMasterData;

export type DiseaseGroup = {
  id: number;
  name: string;
  diseases: Array<Disease>;
};

export type Disease = {
  id: number;
  name: string;
  productGroups: Array<ProductGroup> | null;
};

export type ProductGroup = {
  id: number;
  name: string;
  competitiveProducts: Array<Product> | null;
};

export type Product = SimpleMasterData;

export type Category = {
  id: number;
  name: string;
  groupId: number;
  activities: Array<Activity>;
};

export type Activity = SimpleMasterData;

// GET /comments
export type MyComment = {
  id: number;
  body: string;
  commentedBy: Author;
  commentedAt: string;
  status: PostStatus;
  publishedAt: string;
  commentedPost: CommentedPost;
};

export type CommentedPost = QuotedPost;

// GET /inquiries
// GET /inquiries/{id}
export type Inquiry = {
  id: number;
  inquiredBy: Author;
  inquiredAt: string;
  title: string;
  body: string;
  url: string;
  dueOn: string;
  recipients: Array<Recipient>;
  groups: Array<UserGroup>;
};

export type Recipient = {
  id: number;
  firstName: string;
  lastName: string;
  organizationName: string;
  jobTitle: string;
  answer: Answer;
};

export type Answer = {
  id: number;
  answeredAt: string;
  body: string;
};

// GET notifications
export type Notification = {
  id: number;
  title: string;
  body: string;
  formatted_body: string;
  notifiedAt: string;
  notifiedBy: Author;
  isRead: boolean;
};

// GET /posts
// GET /users/{id}/posts
export type Post = {
  id: number;
  sourceSegments: SourceSegments;
  ciCategories: CiCategories;
  title: string;
  body: string;
  postedBy: Author;
  publishedAt: string;
  postedAt: string;
  status: PostStatus;
  numberOfRetweets: number;
  numberOfComments: number;
  numberOfLikeReactions: number;
  numberOfSameReactions: number;
  numberOfAskReactions: number;
  numberOfAttached: number;
  quotedPost: QuotedPost;
  reaction: {
    isCommented: boolean;
    isReacted: boolean;
    isLiked: boolean;
    likedId?: number;
    reactionId?: number;
    isRetweeted: boolean;
  };
  attachments?: Array<SignedUrl>;
};

export type SourceSegments = {
  occupationGroup: OccupationGroup;
  occupation: Occupation;
  workplace: Workplace;
};

export type SourceSegmentsKey = keyof SourceSegments;

export type CiCategories = {
  diseaseGroup: DiseaseGroup;
  disease: Disease;
  productGroup: ProductGroup;
  competitiveProduct: Product;
  category: Category;
  activity: Activity;
};

export type CiCategoriesKey = keyof CiCategories;

type PostCategories = SourceSegments & CiCategories;

export type PostCategoriesKey = keyof PostCategories;

export type QuotedPost = {
  id: number;
  status: PostStatus;
  diseaseGroup: string;
  disease: string;
  occupationGroup: string;
  occupation: string;
  workplace: string;
  productGroup: string;
  competitiveProduct: string;
  category: string;
  activity: string;
  title: string;
  body: string;
  postedBy: Author;
  publishedAt: string;
};

// GET /posts/{id}
export type PostFindById = Post & {
  comments: Array<Comment> | null;
  likedUser: Array<ReactedUser> | null;
  sameEventsUser: Array<ReactedUser> | null;
  hearMoreUser: Array<ReactedUser> | null;
  geo: GeographicReaction;
};

export type ReactedUser = Author & {
  reactedAt: string;
  areaId?: number;
  regionId?: number;
};

export type Comment = {
  id: number;
  body: string;
  commentedBy: Author;
  status: PostStatus;
  publishedAt: string;
};

export type GeographicReaction = {
  regions: Array<ReactedRegion>;
};

export type ReactedRegion = ReactedArea & {
  areas: Array<ReactedArea> | null;
};

export type ReactedArea = {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  count: number;
};

// GET /reactionTypes
export type ReactionType = {
  id: number;
  role: UserRole;
  languageCode: Language;
  name: string;
  shortName: string;
};

// GET /tasks
export type Task = {
  id: number;
  title: string;
  body: string;
  type: TaskType;
  requestedAt: string;
  requestedBy: Author;
  severity: Severity;
  reference: ReferencedPost | Inquiry | MyComment;
  status: TaskStatus;
  completedAt: string;
  completedBy: Author;
};

export type ReferencedPost = {
  id: number;
  title: string;
  body: string;
  sourceSegments: SourceSegments;
  ciCategories: CiCategories;
  postedBy: Author;
  postedAt: string;
  status: PostStatus;
  quotedPost?: QuotedPost;
  attachments?: Array<SignedUrl>;
};

// GET /userGroups
export type UserGroup = {
  id: number;
  name: string;
};

// GET /userGroups/{id}/users
// GET /users
export type User = Author & {
  email: string;
  description: string;
  agreedAt: string;
  approverId: number;
};

export type Author = {
  id: number;
  role: UserRole;
  firstName: string;
  lastName: string;
  organizationName: string;
  jobTitle: string;
  uuid: string;
  database: string;
  regionId: number;
  areaId: number;
};

export type UserId = {
  id: number;
};

export type TaskId = {
  id: number;
};

export enum NotificationType {
  REACTED = 'reacted',
  REVIEW_COMPLETED = 'review-completed',
  PUBLISHED = 'published',
  ANSWERED = 'answered',
  REVIEW_REQUESTED = 'review-requested',
  INQUIRY = 'inquiry',
  REMINDER = 'reminder',
}

export type NotificationInfo = {
  title: string;
  body: string;
  badge: {
    unreadPosts: number;
    todoTasks: number;
    unreadNotifications: number;
  };
  type: NotificationType;
  referencedId?: number;
};

export type NotificationRequest = NotificationInfo & {
  subscription: PushSubscription;
};

// POST /signedUrl
export type SignedUrl = {
  id?: number;
  objectKey: string;
  signedUrl: string;
};
